import {
  Button,
  Icon,
  Input,
  Select,
  SelectOption,
  StepperWC,
} from '@pypestream/design-system';
import { FC, useEffect, useRef, useState } from 'react';

import { useSolutionWizardForm } from '../components/modals/create-solution-modal/use-solution-wizard-form';
import { useSolutionNameValidation } from '../hooks';

/**
 * "Create a blank solution" (prototype `renderBlank`): a title, a brand and a
 * model, then an empty canvas — no Supervisor, no Knowledge Agent, no nodes
 * at all. Create goes through the wizard's own `createSolution` with the
 * `blank` type; the backend still builds its standard rows for that, so
 * `empty` has them deleted as soon as they load.
 */

/** The model types the wizard offers today. */
const MODEL_TYPES = ['gemini-2.5-flash'];

// The design system's inputs are web components: their `input` event's
// target is the host element, which carries `value`.
const valueOf = (e: unknown) =>
  String((e as { target?: { value?: unknown } }).target?.value ?? '');

export const BlankSolutionForm: FC<{ onBack: () => void }> = ({ onBack }) => {
  // The wizard's create path without its stepper: nothing here to move.
  const noStepper = useRef<StepperWC>(null);
  const {
    createSolution,
    credentials,
    isLoading,
    setIsLoading,
    isBotFlowError,
  } = useSolutionWizardForm(noStepper);

  const [title, setTitle] = useState('');
  const [brand, setBrand] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [failed, setFailed] = useState(false);
  // Only a create started here counts: the machine can still be parked in
  // `botFlowError` from an earlier attempt when this form opens.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!attempt || !isBotFlowError) return;
    setIsLoading(false);
    setFailed(true);
  }, [attempt, isBotFlowError, setIsLoading]);

  const { isValidName, hasLengthError, validateSolutionName, getTag } =
    useSolutionNameValidation(title);

  // A provider picked for them when there's exactly one choice.
  useEffect(() => {
    if (!credentialId && credentials?.length === 1) {
      setCredentialId(credentials[0].id || '');
    }
  }, [credentials, credentialId]);

  const create = () => {
    if (!isValidName || isLoading) return;
    setFailed(false);
    createSolution({
      formData: {
        botName: title,
        type: 'blank',
        llmCredentialId: credentialId || undefined,
        ...(brand.trim() ? { brandName: brand.trim() } : {}),
      },
      callback: () => undefined,
      empty: true,
    });
    // After the send, so this render already sees the machine creating.
    setAttempt((n) => n + 1);
  };

  return (
    <div className="c-home-start" data-testid="bobcat-home-blank">
      <Button size="small" variant="ghost" onClick={onBack}>
        <Icon slot="prefix" name="arrow-left" size="xsmall" />
        Ways to start
      </Button>
      <form
        className="c-home-start__form c-home-steps"
        onSubmit={(e) => e.preventDefault()}
      >
        <h1 className="c-home-steps__title">Create a blank solution</h1>
        <p className="c-home-steps__sub">
          Name it and pick its model. It starts as an empty canvas, and you
          build every agent yourself.
        </p>

        <label className="c-home-start__label" htmlFor="bobcat-blank-title">
          Solution title*
        </label>
        <Input
          id="bobcat-blank-title"
          test-id="bobcat-home-blank-title"
          variant="outlined"
          placeholder="Solution title"
          autofocus
          value={title}
          disabled={isLoading}
          hasError={hasLengthError}
          onInput={(e) => setTitle(validateSolutionName(valueOf(e)))}
        />
        <div className="c-home-start__rules">
          {getTag('1–100 characters', 'passedLengthCheck')}
          {getTag('Unique in this project', 'passedUniquenessCheck')}
        </div>

        <label className="c-home-start__label" htmlFor="bobcat-blank-brand">
          Brand name
        </label>
        <Input
          id="bobcat-blank-brand"
          test-id="bobcat-home-blank-brand"
          variant="outlined"
          placeholder="The name customers know you by"
          value={brand}
          disabled={isLoading}
          onInput={(e) => setBrand(valueOf(e))}
        />

        <div className="c-home-start__row">
          <div>
            <span className="c-home-start__label">AI provider</span>
            {credentials?.length ? (
              <Select
                test-id="bobcat-home-blank-provider"
                variant="outlined"
                placeholder="Select a provider"
                value={credentialId}
                disabled={isLoading}
                onChange={(e) => setCredentialId(valueOf(e))}
              >
                {credentials.map((c) => (
                  <SelectOption key={c.id} value={c.id || ''}>
                    {c.llmProvider?.name || 'Gemini'}
                  </SelectOption>
                ))}
              </Select>
            ) : (
              <p className="c-home-start__hint">
                No AI provider connected to this project yet. You can add one
                later in the solution’s settings.
              </p>
            )}
          </div>
          <div>
            <span className="c-home-start__label">Model type</span>
            <Select
              test-id="bobcat-home-blank-model"
              variant="outlined"
              value={MODEL_TYPES[0]}
              disabled={isLoading || !credentials?.length}
            >
              {MODEL_TYPES.map((m) => (
                <SelectOption key={m} value={m}>
                  {m}
                </SelectOption>
              ))}
            </Select>
          </div>
        </div>

        {failed && (
          <p className="c-home-start__error" role="alert">
            Couldn’t create the solution. Try again in a moment — if it keeps
            failing, the error is in the notifications.
          </p>
        )}
        <div className="c-home-start__actions">
          <Button
            test-id="bobcat-home-blank-create"
            // Not a submit button: the design system's button submits its
            // form as well as firing onClick.
            type="button"
            disabled={!isValidName || isLoading}
            onClick={create}
          >
            {isLoading ? 'Creating…' : 'Create solution'}
          </Button>
        </div>
      </form>
    </div>
  );
};
